import React, { useEffect, useMemo } from "react";
import { Stack, Typography, Divider, Box } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import FormSelectField from "../components/common/FormSelectField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch, useAppSelector } from "../redux/store";
import { createStudent, updateStudent } from "../redux/actions/studentActions";
import { getBranches } from "../redux/actions/branchActions";
import { studentActions } from "../redux/slices/student";
import { cleanFormData } from "../helpers/formHelpers";

import { StudentResponseDto } from "../shared/dtos/student/StudentResponseDto";
import { StudentCreateRequestDto } from "../shared/dtos/student/StudentCreateRequestDto";
import { StudentUpdateRequestDto } from "../shared/dtos/student/StudentUpdateRequestDto";
import { Gender } from "../shared/enums/Gender";
import { BloodGroupType } from "../shared/enums/BloodGroupType";

interface StudentFormValues {
  branchId: string;
  firstName: string;
  middleName: string;
  lastName: string;
  password: string;
  dateOfBirth: string;
  gender: Gender;
  bloodGroupType: BloodGroupType;
  nationality: string;
  address: {
    streetLine1: string;
    streetLine2: string;
    city: string;
    stateOrProvince: string;
    postalCode: string;
    country: string;
  };
}

const addressSchema = Yup.object().shape({
  streetLine1: Yup.string().trim().required("Street Line 1 is required"),
  streetLine2: Yup.string().trim().nullable(),
  city: Yup.string().trim().required("City is required"),
  stateOrProvince: Yup.string().trim().required("State/Province is required"),
  postalCode: Yup.string().trim().required("Postal Code is required"),
  country: Yup.string().trim().required("Country is required"),
});

const baseValidation = {
  branchId: Yup.string().required("Campus / Branch is required"),
  firstName: Yup.string().trim().required("First Name is required"),
  middleName: Yup.string().trim().nullable(),
  lastName: Yup.string().trim().required("Last Name is required"),
  dateOfBirth: Yup.string().required("Date of Birth is required"),
  gender: Yup.mixed<Gender>()
    .oneOf(Object.values(Gender), "Invalid gender selection")
    .required("Gender is required"),
  bloodGroupType: Yup.mixed<BloodGroupType>()
    .oneOf(Object.values(BloodGroupType), "Invalid blood group")
    .required("Blood Group is required"),
  nationality: Yup.string().trim().required("Nationality is required"),
  address: addressSchema,
};

const createStudentSchema = Yup.object().shape({
  ...baseValidation,
  password: Yup.string()
    .min(6, "Password must be at least 6 characters")
    .required("Password is required"),
});

const updateStudentSchema = Yup.object().shape({
  ...baseValidation,
  password: Yup.string()
    .test(
      "min-length-if-present",
      "Password must be at least 6 characters",
      (value) => {
        if (!value) return true;
        return value.length >= 6;
      },
    )
    .nullable(),
});

interface CreateOrUpdateStudentFormProps {
  student?: StudentResponseDto | null;
  isEditing?: boolean;
  loading?: boolean;
  error?: string | null;
}

const CreateOrUpdateStudentForm: React.FC<CreateOrUpdateStudentFormProps> = ({
  student = null,
  isEditing = false,
  loading = false,
  error = null,
}) => {
  const dispatch = useAppDispatch();
  const { userInfo } = useAppSelector((state) => state.auth);
  const { items: branches } = useAppSelector((state) => state.branch);

  const isSuperAdmin = userInfo?.userRole === "SUPER_ADMIN";

  useEffect(() => {
    if (isSuperAdmin && (!branches || branches.length === 0)) {
      dispatch(getBranches());
    }
  }, [dispatch, branches, isSuperAdmin]);

  const branchOptions = useMemo(
    () => branches?.map((b) => ({ value: b.id, label: b.name })) || [],
    [branches],
  );

  const genderOptions = useMemo(
    () =>
      Object.values(Gender).map((g) => ({
        value: g,
        label: g.charAt(0) + g.slice(1).toLowerCase(),
      })),
    [],
  );

  const bloodGroupOptions = useMemo(
    () =>
      Object.values(BloodGroupType).map((bg) => ({
        value: bg,
        label: bg.replace(/_/g, " "),
      })),
    [],
  );

  const initialValues: StudentFormValues = {
    branchId:
      student?.branchId || (isSuperAdmin ? "" : userInfo?.branchId) || "",
    firstName: student?.firstName || "",
    middleName: student?.middleName || "",
    lastName: student?.lastName || "",
    password: "",
    dateOfBirth: student?.dateOfBirth
      ? student.dateOfBirth.substring(0, 10)
      : "",
    gender: (student?.gender as Gender) || Gender.MALE,
    bloodGroupType:
      (student?.bloodGroup as BloodGroupType) || BloodGroupType.O_POSITIVE,
    nationality: student?.nationality || "Kenyan",
    address: {
      streetLine1: student?.address?.streetLine1 || "",
      streetLine2: student?.address?.streetLine2 || "",
      city: student?.address?.city || "Nairobi",
      stateOrProvince: student?.address?.stateOrProvince || "Nairobi",
      postalCode: student?.address?.postalCode || "",
      country: student?.address?.country || "Kenya",
    },
  };

  const handleSubmit = (values: StudentFormValues) => {
    const cleaned = cleanFormData(values);

    if (isEditing && student) {
      const { password, ...rest } = cleaned;
      const updatePayload: StudentUpdateRequestDto = {
        ...rest,
        ...(password ? { password } : {}),
      };

      dispatch(updateStudent({ id: student.id, values: updatePayload }));
      return;
    }

    dispatch(createStudent(cleaned as StudentCreateRequestDto));
  };

  return (
    <Stack spacing={2} sx={{ width: "100%", textAlign: "center" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={
          isEditing
            ? () => dispatch(studentActions.setErrorUpdate(null))
            : () => dispatch(studentActions.setErrorCreate(null))
        }
      />

      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={isEditing ? updateStudentSchema : createStudentSchema}
        onSubmit={handleSubmit}
      >
        {({ dirty, isValid }: FormikProps<StudentFormValues>) => (
          <Form>
            <Stack spacing={2}>
              {isSuperAdmin && (
                <>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "text.secondary",
                      textAlign: "left",
                      fontWeight: 700,
                    }}
                  >
                    Campus Assignment
                  </Typography>
                  <FormSelectField
                    name="branchId"
                    label="Campus / Branch"
                    options={branchOptions}
                  />
                  <Divider sx={{ my: 1 }} />
                </>
              )}

              <Typography
                variant="subtitle2"
                sx={{
                  color: "text.secondary",
                  textAlign: "left",
                  fontWeight: 700,
                }}
              >
                Core Information
              </Typography>

              {!isSuperAdmin && (
                <FormSelectField
                  name="branchId"
                  label="Campus / Branch"
                  options={branchOptions}
                  disabled
                />
              )}

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                  gap: 2,
                }}
              >
                <FormTextField name="firstName" label="First Name" />
                <FormTextField
                  name="middleName"
                  label="Middle Name (Optional)"
                />
                <FormTextField name="lastName" label="Last Name" />
              </Box>

              <FormTextField
                name="password"
                label={
                  isEditing
                    ? "New Password (Leave blank to keep existing)"
                    : "Account Password"
                }
                type="password"
              />

              <Divider sx={{ my: 1 }} />

              <Typography
                variant="subtitle2"
                sx={{
                  color: "text.secondary",
                  textAlign: "left",
                  fontWeight: 700,
                }}
              >
                Demographics
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <FormTextField
                  name="dateOfBirth"
                  label="Date of Birth"
                  type="date"
                  slotProps={{ inputLabel: { shrink: true } }}
                />
                <FormSelectField
                  name="gender"
                  label="Gender"
                  options={genderOptions}
                />
                <FormSelectField
                  name="bloodGroupType"
                  label="Blood Group"
                  options={bloodGroupOptions}
                />
                <FormTextField name="nationality" label="Nationality" />
              </Box>

              <Divider sx={{ my: 1 }} />

              <Typography
                variant="subtitle2"
                sx={{
                  color: "text.secondary",
                  textAlign: "left",
                  fontWeight: 700,
                }}
              >
                Home Address
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <FormTextField
                  name="address.streetLine1"
                  label="Street Address (Line 1)"
                />
                <FormTextField
                  name="address.streetLine2"
                  label="Apt / Suite (Optional)"
                />
                <FormTextField name="address.city" label="City" />
                <FormTextField
                  name="address.stateOrProvince"
                  label="State / Province"
                />
                <FormTextField
                  name="address.postalCode"
                  label="Postal / Zip Code"
                />
                <FormTextField name="address.country" label="Country" />
              </Box>

              <SubmitButton
                title={isEditing ? "Save Changes" : "Register Student"}
                loadingText={isEditing ? "Saving..." : "Registering..."}
                loading={loading}
                disabled={!dirty || !isValid || loading}
                startIcon={
                  isEditing ? <SaveOutlinedIcon /> : <PersonAddOutlinedIcon />
                }
                sx={{ mt: 2 }}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Stack>
  );
};

export default CreateOrUpdateStudentForm;
